import { type FC, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import type { ViewerObjectProps } from '../types';
import { RectangularGrid } from './RectangularGrid';
import { TextLabel, type TextLabelProps } from './TextLabel';

export interface FloorLevelStyle {
  color: number;
  opacity: number;
  lineWidth: number;
}

export interface FloorLevelProps extends ViewerObjectProps {
  label?: Omit<TextLabelProps, 'position' | 'text'>;
  style: FloorLevelStyle;
  zLevel: number;
  gridCellSize: number;
  bboxPadding?: Parameters<THREE.Vector3['set']>;
  labelOffset?: Parameters<THREE.Vector3['set']>;
}

export interface FloorLevelExtendedProps extends FloorLevelProps {
  boundingBox: THREE.Box3;
}

export const FloorLevel: FC<FloorLevelExtendedProps> = ({
  name,
  label,
  style,
  zLevel,
  gridCellSize,
  bboxPadding,
  labelOffset,
  boundingBox,
}) => {
  const bbox = boundingBox.clone();

  const isFiniteBbox =
    Number.isFinite(bbox.min.x) &&
    Number.isFinite(bbox.min.y) &&
    Number.isFinite(bbox.min.z) &&
    Number.isFinite(bbox.max.x) &&
    Number.isFinite(bbox.max.y) &&
    Number.isFinite(bbox.max.z);

  if (!isFiniteBbox) {
    const zero = new THREE.Vector3();
    bbox.set(zero, zero);
  }

  if (bboxPadding !== undefined) {
    const padding = new THREE.Vector3(...bboxPadding);
    bbox.min.sub(padding);
    bbox.max.add(padding);
  }

  bbox.min.multiplyScalar(1 / gridCellSize).floor();
  bbox.max.multiplyScalar(1 / gridCellSize).ceil();

  const xNumberCells = Math.trunc(bbox.max.x - bbox.min.x);
  const yNumberCells = Math.trunc(bbox.max.y - bbox.min.y);
  const xCellSize = gridCellSize;
  const yCellSize = gridCellSize;

  bbox.min.multiplyScalar(gridCellSize);
  bbox.max.multiplyScalar(gridCellSize);

  const { x, y } = bbox.min;

  const isVisible =
    xNumberCells !== 0 && yNumberCells !== 0 && zLevel > bbox.min.z && zLevel < bbox.max.z;

  const billboardOffset = new THREE.Vector3(...(labelOffset || [0, 0, 0]));

  // billboardOffset.x += xNumberCells * xCellSize;
  billboardOffset.y += yNumberCells * yCellSize;

  const grid = useMemo(() => {
    // console.log('RectangularGrid', {
    //   bbox,
    //   xNumberCells,
    //   yNumberCells,
    //   xCellSize,
    //   yCellSize,
    //   style,
    // });
    return new RectangularGrid(
      xNumberCells,
      yNumberCells,
      xCellSize,
      yCellSize,
      style.color,
      style.opacity,
      style.lineWidth
    );
  }, [xNumberCells, yNumberCells, xCellSize, yCellSize, style]);

  useEffect(() => {
    return () => {
      grid.dispose();
    };
  }, [grid]);

  return (
    <group position={[x, y, zLevel]}>
      {isVisible && <primitive object={grid} />}
      {isVisible && label !== undefined && (
        <TextLabel position={billboardOffset.toArray()} {...label} text={name} />
      )}
    </group>
  );
};
